import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useUiMessages } from '../messages';

import { NotificationHost } from './NotificationHost';
import styles from './NotificationProvider.module.css';
import { notificationStore } from './store';
import type { NotificationEntry } from './store';

export type NotificationPlacement =
  'top-right' | 'bottom-right' | 'top-left' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface NotificationProviderProps {
  children?: ReactNode;
  /** Corner/edge the stack anchors to. Newest toast sits at the outer edge. Defaults to `top-right`. */
  placement?: NotificationPlacement;
  /** Default auto-dismiss (ms) for action-less toasts. Defaults to 5000. */
  duration?: number;
  /** Accessible name for the toast region. */
  label?: string;
}

const DEFAULT_DURATION = 5000;

interface RenderedItem {
  entry: NotificationEntry;
  /** Dropped from the store but still animating out. */
  leaving: boolean;
}

/**
 * Mounts the notification stack. Render once near the app root. Portals a single fixed region to
 * `document.body` that anchors to a screen edge, stacks toasts (newest at the outer edge) and
 * scrolls without a visible scrollbar when they overflow — with padding so card shadows aren't
 * clipped. Dismissed toasts are retained briefly so they can play their exit animation before
 * unmounting. Each toast owns its own auto-dismiss timer (see `NotificationHost`).
 */
export function NotificationProvider({
  children,
  placement = 'top-right',
  duration = DEFAULT_DURATION,
  label,
}: NotificationProviderProps) {
  const messages = useUiMessages();
  const resolvedLabel = label ?? messages.notificationProvider?.region ?? 'Notifications';
  const stack = useSyncExternalStore(
    notificationStore.subscribe,
    notificationStore.getSnapshot,
    notificationStore.getSnapshot,
  );

  // Local presence list: mirrors the store, but keeps entries that just left the store around
  // (flagged `leaving`) so they can animate out before we unmount them.
  const [items, setItems] = useState<RenderedItem[]>([]);

  useEffect(() => {
    setItems((prev) => {
      const inStore = new Map(stack.map((entry) => [entry.id, entry]));
      const next: RenderedItem[] = stack.map((entry) => ({ entry, leaving: false }));
      // Re-insert still-present leaving items at (roughly) their previous slot.
      prev.forEach((item, index) => {
        if (!inStore.has(item.entry.id)) {
          next.splice(Math.min(index, next.length), 0, { entry: item.entry, leaving: true });
        }
      });
      return next;
    });
  }, [stack]);

  const handleExited = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.entry.id !== id));
  }, []);

  // Top edges show newest first (at the top); bottom edges keep insertion order (newest at bottom).
  const ordered = placement.startsWith('top') ? [...items].reverse() : items;

  return (
    <>
      {children}
      {items.length > 0 && typeof document !== 'undefined'
        ? createPortal(
            <section
              className={styles.viewport}
              data-placement={placement}
              aria-label={resolvedLabel}
            >
              <ol className={styles.list}>
                {ordered.map((item) => (
                  <NotificationHost
                    key={item.entry.id}
                    entry={item.entry}
                    leaving={item.leaving}
                    onExited={handleExited}
                    defaultDuration={duration}
                  />
                ))}
              </ol>
            </section>,
            document.body,
          )
        : null}
    </>
  );
}
